import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { uid } from './uid';

/**
 * Load a 3D model file and return the Three.js object
 */
export const loadModel = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const fileName = file.name.toLowerCase();

    reader.onload = (event) => {
      const contents = event.target.result;

      try {
        if (fileName.endsWith('.obj')) {
          const loader = new OBJLoader();
          const object = loader.parse(contents);
          resolve(object);
        } else if (fileName.endsWith('.gltf') || fileName.endsWith('.glb')) {
          const loader = new GLTFLoader();
          // For GLB (binary), we need array buffer
          if (fileName.endsWith('.glb')) {
            loader.parse(contents, '', (gltf) => {
              resolve(gltf.scene);
            }, reject);
          } else {
            // For GLTF (text), parse as text
            loader.parse(contents, '', (gltf) => {
              resolve(gltf.scene);
            }, reject);
          }
        } else {
          reject(new Error('Unsupported file format. Please use OBJ, GLTF, or GLB files.'));
        }
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));

    // Read as appropriate format
    if (fileName.endsWith('.glb')) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  });
};

/**
 * Compute convex hull of 2D points using Graham scan algorithm
 * @param {Array} points - Array of {x, y} points
 * @returns {Array} - Convex hull vertices in counter-clockwise order
 */
const computeConvexHull2D = (points) => {
  if (points.length < 3) return points;

  // Find the point with lowest y (and leftmost if tie)
  let start = 0;
  for (let i = 1; i < points.length; i++) {
    if (points[i].y < points[start].y ||
        (points[i].y === points[start].y && points[i].x < points[start].x)) {
      start = i;
    }
  }

  // Swap start to position 0
  [points[0], points[start]] = [points[start], points[0]];
  const pivot = points[0];

  // Sort by polar angle with respect to pivot
  const sorted = points.slice(1).sort((a, b) => {
    const angleA = Math.atan2(a.y - pivot.y, a.x - pivot.x);
    const angleB = Math.atan2(b.y - pivot.y, b.x - pivot.x);
    if (Math.abs(angleA - angleB) < 1e-10) {
      // Same angle, sort by distance
      const distA = (a.x - pivot.x) ** 2 + (a.y - pivot.y) ** 2;
      const distB = (b.x - pivot.x) ** 2 + (b.y - pivot.y) ** 2;
      return distA - distB;
    }
    return angleA - angleB;
  });

  // Cross product to determine turn direction
  const cross = (o, a, b) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

  const hull = [pivot];
  for (const point of sorted) {
    while (hull.length > 1 && cross(hull[hull.length - 2], hull[hull.length - 1], point) <= 0) {
      hull.pop();
    }
    hull.push(point);
  }

  return hull;
};

/**
 * Simplify polygon by removing collinear points
 * @param {Array} vertices - Array of {x, y} points
 * @param {number} tolerance - Angle tolerance in radians
 * @returns {Array} - Simplified polygon vertices
 */
const simplifyPolygon = (vertices, tolerance = 0.01) => {
  if (vertices.length < 3) return vertices;

  const result = [];
  const n = vertices.length;

  for (let i = 0; i < n; i++) {
    const prev = vertices[(i - 1 + n) % n];
    const curr = vertices[i];
    const next = vertices[(i + 1) % n];

    // Calculate vectors
    const v1 = { x: curr.x - prev.x, y: curr.y - prev.y };
    const v2 = { x: next.x - curr.x, y: next.y - curr.y };

    // Normalize
    const len1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
    const len2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);

    if (len1 < 1e-10 || len2 < 1e-10) continue;

    v1.x /= len1; v1.y /= len1;
    v2.x /= len2; v2.y /= len2;

    // Cross product gives sin of angle
    const cross = v1.x * v2.y - v1.y * v2.x;

    // Keep vertex if angle is significant (not collinear)
    if (Math.abs(cross) > tolerance) {
      result.push(curr);
    }
  }

  return result.length >= 3 ? result : vertices;
};

/**
 * Calculate polygon area using shoelace formula
 */
const calculatePolygonArea = (vertices) => {
  let area = 0;
  const n = vertices.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += vertices[i].x * vertices[j].y;
    area -= vertices[j].x * vertices[i].y;
  }
  return Math.abs(area / 2);
};

/**
 * Classify shape type based on vertices
 * @param {Array} vertices - Array of {x, y} points
 * @param {number} boundingWidth - Bounding box width
 * @param {number} boundingHeight - Bounding box height
 * @returns {string} - 'rectangle', 'trapezoid', or 'polygon'
 */
const classifyShape = (vertices, boundingWidth, boundingHeight) => {
  if (vertices.length !== 4) return 'polygon';

  // First check: if polygon area equals bounding box area (within 2%), it's a rectangle
  // This is a robust check that doesn't depend on angle calculations
  const polyArea = calculatePolygonArea(vertices);
  const bboxArea = boundingWidth * boundingHeight;
  if (bboxArea > 0 && Math.abs(polyArea - bboxArea) / bboxArea < 0.02) {
    return 'rectangle';
  }

  // Second check: all angles ~90 degrees (with more tolerance)
  const isRightAngle = (p1, p2, p3) => {
    const v1 = { x: p1.x - p2.x, y: p1.y - p2.y };
    const v2 = { x: p3.x - p2.x, y: p3.y - p2.y };
    const dot = v1.x * v2.x + v1.y * v2.y;
    const len1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
    const len2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);
    if (len1 < 1e-10 || len2 < 1e-10) return false;
    const cosAngle = dot / (len1 * len2);
    return Math.abs(cosAngle) < 0.15; // Within ~8.5 degrees of 90 (more tolerant)
  };

  let rightAngles = 0;
  for (let i = 0; i < 4; i++) {
    if (isRightAngle(vertices[(i - 1 + 4) % 4], vertices[i], vertices[(i + 1) % 4])) {
      rightAngles++;
    }
  }

  if (rightAngles === 4) return 'rectangle';

  // Check for trapezoid (one pair of parallel sides)
  const areParallel = (p1, p2, p3, p4) => {
    const v1 = { x: p2.x - p1.x, y: p2.y - p1.y };
    const v2 = { x: p4.x - p3.x, y: p4.y - p3.y };
    const cross = v1.x * v2.y - v1.y * v2.x;
    const len1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
    const len2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);
    if (len1 < 1e-10 || len2 < 1e-10) return false;
    return Math.abs(cross / (len1 * len2)) < 0.1; // Within ~6 degrees of parallel
  };

  // Check opposite sides for parallelism
  const side1Parallel = areParallel(vertices[0], vertices[1], vertices[2], vertices[3]);
  const side2Parallel = areParallel(vertices[1], vertices[2], vertices[3], vertices[0]);

  if (side1Parallel || side2Parallel) return 'trapezoid';

  return 'polygon';
};

/**
 * Extract 2D polygon outline from a flat 3D mesh geometry
 * Projects vertices onto the dominant plane and extracts convex hull
 * @param {THREE.BufferGeometry} geometry - The mesh geometry
 * @returns {Object} - { vertices, shapeType, width, height, bounds }
 */
const extractPolygonOutline = (geometry) => {
  const position = geometry.getAttribute('position');
  if (!position || position.count < 3) return null;

  // Collect all unique vertices using spatial hash
  const PRECISION = 10000;
  const uniqueVertices = new Map();

  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i);
    const y = position.getY(i);
    const z = position.getZ(i);
    const key = `${Math.round(x * PRECISION)},${Math.round(y * PRECISION)},${Math.round(z * PRECISION)}`;
    if (!uniqueVertices.has(key)) {
      uniqueVertices.set(key, new THREE.Vector3(x, y, z));
    }
  }

  const vertices3D = Array.from(uniqueVertices.values());
  if (vertices3D.length < 3) return null;

  // Compute bounding box and center
  const min = new THREE.Vector3(Infinity, Infinity, Infinity);
  const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
  vertices3D.forEach(v => { min.min(v); max.max(v); });

  const size = new THREE.Vector3().subVectors(max, min);
  const center = new THREE.Vector3().addVectors(min, max).multiplyScalar(0.5);

  // Determine the flat axis (smallest dimension)
  const dims = [
    { axis: 'x', value: size.x },
    { axis: 'y', value: size.y },
    { axis: 'z', value: size.z }
  ].sort((a, b) => a.value - b.value);

  const flatAxis = dims[0].axis;
  const thickness = dims[0].value;

  // Project to 2D by dropping the flat axis
  const project2D = (v) => {
    if (flatAxis === 'z') return { x: v.x, y: v.y };
    if (flatAxis === 'y') return { x: v.x, y: v.z };
    return { x: v.y, y: v.z }; // flatAxis === 'x'
  };

  // Get unique 2D points (many 3D vertices map to same 2D point on flat piece)
  const points2DMap = new Map();
  vertices3D.forEach(v => {
    const p2d = project2D(v);
    const key = `${Math.round(p2d.x * PRECISION)},${Math.round(p2d.y * PRECISION)}`;
    if (!points2DMap.has(key)) {
      points2DMap.set(key, p2d);
    }
  });

  const points2D = Array.from(points2DMap.values());
  if (points2D.length < 3) return null;

  // Compute convex hull
  let hull = computeConvexHull2D([...points2D]);

  // Simplify to remove near-collinear points
  hull = simplifyPolygon(hull);

  if (hull.length < 3) return null;

  // Compute 2D bounding box
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  hull.forEach(p => {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  });

  const width = maxX - minX;
  const height = maxY - minY;

  // Normalize vertices to be relative to bounding box origin (0,0)
  const normalizedVertices = hull.map(p => ({
    x: p.x - minX,
    y: p.y - minY
  }));

  // Classify the shape (pass bounding dimensions for area-based rectangle detection)
  const shapeType = classifyShape(normalizedVertices, width, height);

  return {
    vertices: normalizedVertices,
    shapeType,
    width,
    height,
    thickness,
    center,
    bounds: { min, max, size },
    flatAxis
  };
};

/**
 * Compute the oriented bounding box (OBB) dimensions for a mesh
 * Uses PCA (Principal Component Analysis) approach for better accuracy
 */
const computeOrientedBoundingBox = (geometry) => {
  const position = geometry.getAttribute('position');
  if (!position || position.count === 0) return null;

  // Collect all vertices
  const vertices = [];
  for (let i = 0; i < position.count; i++) {
    vertices.push(new THREE.Vector3(
      position.getX(i),
      position.getY(i),
      position.getZ(i)
    ));
  }

  // Compute centroid
  const centroid = new THREE.Vector3();
  vertices.forEach(v => centroid.add(v));
  centroid.divideScalar(vertices.length);

  // For axis-aligned bounding box (simpler but works well for most furniture)
  const min = new THREE.Vector3(Infinity, Infinity, Infinity);
  const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);

  vertices.forEach(v => {
    min.min(v);
    max.max(v);
  });

  const size = new THREE.Vector3().subVectors(max, min);

  // Sort dimensions to get width, height, thickness (largest to smallest)
  const dims = [
    { axis: 'x', value: size.x },
    { axis: 'y', value: size.y },
    { axis: 'z', value: size.z }
  ].sort((a, b) => b.value - a.value);

  return {
    width: dims[0].value,
    height: dims[1].value,
    thickness: dims[2].value,
    center: new THREE.Vector3().addVectors(min, max).multiplyScalar(0.5),
    size,
    min,
    max
  };
};

/**
 * Try to auto-detect the unit of the model based on its dimensions
 * Assumes the model represents furniture (typical sizes: 100-3000mm)
 */
export const autoDetectUnit = (object) => {
  const box = new THREE.Box3().setFromObject(object);
  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);

  // Heuristics based on typical furniture dimensions
  // A typical bookshelf/cabinet is 0.3-2.5m, 12-100in, 30-250cm, 300-2500mm

  if (maxDim > 5000) {
    // Very large numbers, probably millimeters
    return 'mm';
  } else if (maxDim > 500) {
    // Large numbers: 500-5000 could be mm (small piece) or cm (large furniture)
    // Assume mm for safety as it's more common in CAD
    return 'mm';
  } else if (maxDim > 50) {
    // 50-500: could be cm (0.5-5m furniture) or inches (4ft furniture)
    // Lean towards cm for European CAD, inches for imperial
    return 'cm';
  } else if (maxDim > 10) {
    // 10-50: likely inches (10-50 inches = small-medium furniture)
    return 'in';
  } else if (maxDim > 0.1) {
    // 0.1-10: likely meters (0.1-10m = typical furniture range)
    // This catches Blender exports which default to meters
    return 'm';
  } else {
    // Very small (<0.1), unusual - default to meters
    return 'm';
  }
};

/**
 * Get model bounds and size information
 */
export const getModelBounds = (object) => {
  const box = new THREE.Box3().setFromObject(object);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());

  return {
    box,
    size,
    center,
    maxDimension: Math.max(size.x, size.y, size.z),
    minDimension: Math.min(size.x, size.y, size.z)
  };
};

/**
 * Separate a mesh geometry into connected components (islands)
 * This handles cases where multiple disconnected objects are exported as a single mesh
 * (common with Rhino, some SketchUp exports, etc.)
 *
 * Uses spatial hashing to find vertices at the same position, since OBJLoader
 * often creates non-indexed geometry with duplicated vertices.
 *
 * @param {THREE.BufferGeometry} geometry - The geometry to separate
 * @returns {Array} Array of separate BufferGeometry objects
 */
const separateConnectedComponents = (geometry) => {
  const position = geometry.getAttribute('position');
  if (!position || position.count === 0) return [geometry];

  const vertexCount = position.count;
  const triangleCount = Math.floor(vertexCount / 3);

  if (triangleCount < 2) return [geometry];

  // Spatial hash to find vertices at the same position
  // Key: rounded position string, Value: array of vertex indices at that position
  const PRECISION = 10000; // 4 decimal places
  const spatialHash = new Map();

  const hashPosition = (x, y, z) => {
    return `${Math.round(x * PRECISION)},${Math.round(y * PRECISION)},${Math.round(z * PRECISION)}`;
  };

  // Build spatial hash
  for (let i = 0; i < vertexCount; i++) {
    const key = hashPosition(
      position.getX(i),
      position.getY(i),
      position.getZ(i)
    );
    if (!spatialHash.has(key)) {
      spatialHash.set(key, []);
    }
    spatialHash.get(key).push(i);
  }

  // Union-Find on triangles (not vertices)
  // Two triangles are connected if they share a vertex position
  const parent = new Array(triangleCount).fill(0).map((_, i) => i);
  const rank = new Array(triangleCount).fill(0);

  const find = (x) => {
    if (parent[x] !== x) {
      parent[x] = find(parent[x]);
    }
    return parent[x];
  };

  const union = (x, y) => {
    const px = find(x);
    const py = find(y);
    if (px === py) return;

    if (rank[px] < rank[py]) {
      parent[px] = py;
    } else if (rank[px] > rank[py]) {
      parent[py] = px;
    } else {
      parent[py] = px;
      rank[px]++;
    }
  };

  // Map each vertex position to the triangles that use it
  const positionToTriangles = new Map();

  for (let tri = 0; tri < triangleCount; tri++) {
    for (let v = 0; v < 3; v++) {
      const vertIdx = tri * 3 + v;
      const key = hashPosition(
        position.getX(vertIdx),
        position.getY(vertIdx),
        position.getZ(vertIdx)
      );

      if (!positionToTriangles.has(key)) {
        positionToTriangles.set(key, new Set());
      }
      positionToTriangles.get(key).add(tri);
    }
  }

  // Union triangles that share a vertex position
  positionToTriangles.forEach((triangles) => {
    const triArray = Array.from(triangles);
    for (let i = 1; i < triArray.length; i++) {
      union(triArray[0], triArray[i]);
    }
  });

  // Group triangles by their root
  const components = new Map();
  for (let tri = 0; tri < triangleCount; tri++) {
    const root = find(tri);
    if (!components.has(root)) {
      components.set(root, []);
    }
    components.get(root).push(tri);
  }

  // If only one component, return original
  if (components.size <= 1) {
    return [geometry];
  }

  console.log(`Found ${components.size} disconnected components in mesh`);

  // Create separate geometries for each component
  const separatedGeometries = [];

  components.forEach((triangles) => {
    const newPositions = [];
    const newNormals = [];
    const normal = geometry.getAttribute('normal');

    triangles.forEach(tri => {
      for (let v = 0; v < 3; v++) {
        const vertIdx = tri * 3 + v;
        newPositions.push(
          position.getX(vertIdx),
          position.getY(vertIdx),
          position.getZ(vertIdx)
        );
        if (normal) {
          newNormals.push(
            normal.getX(vertIdx),
            normal.getY(vertIdx),
            normal.getZ(vertIdx)
          );
        }
      }
    });

    const newGeometry = new THREE.BufferGeometry();
    newGeometry.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3));
    if (newNormals.length > 0) {
      newGeometry.setAttribute('normal', new THREE.Float32BufferAttribute(newNormals, 3));
    }
    newGeometry.computeBoundingBox();
    separatedGeometries.push(newGeometry);
  });

  return separatedGeometries;
};

/**
 * Analyze a 3D model and extract flat rectangular pieces
 * This version analyzes individual mesh objects and their bounding boxes
 * It also handles meshes with multiple disconnected components (common in Rhino exports)
 *
 * @param {THREE.Object3D} object - The loaded 3D model
 * @param {Object} options - Analysis options
 * @returns {Array} Array of detected pieces with dimensions
 */
export const analyzeModel = (object, options = {}) => {
  const {
    targetThickness = null,
    thicknessTolerance = 2,
    unit = 'mm',
    flatnessRatio = 0.5 // A piece is "flat" if thickness < width * ratio AND thickness < height * ratio
  } = options;

  // Minimum area threshold scales with unit (in square units)
  // For meters: 0.001 m² = 10cm × 10cm minimum
  // For mm: 100 mm² = 10mm × 10mm minimum
  const minAreaByUnit = {
    'm': 0.001,
    'cm': 1,
    'mm': 100,
    'in': 1,
    'ft': 0.01
  };
  const minArea = minAreaByUnit[unit] || 0.001;

  // Minimum thickness to filter out 2D planes/rendering artifacts
  const minThicknessByUnit = {
    'm': 0.001,    // 1mm
    'cm': 0.1,     // 1mm
    'mm': 1,       // 1mm
    'in': 0.04,    // ~1mm
    'ft': 0.003    // ~1mm
  };
  const minThickness = minThicknessByUnit[unit] || 0.001;

  // Update world matrices
  object.updateMatrixWorld(true);

  const detectedPieces = [];
  let pieceId = 1;

  // Helper function to analyze a single geometry and add valid pieces
  const analyzeGeometry = (geometry, meshName, meshUuid, componentIndex = null) => {
    const obb = computeOrientedBoundingBox(geometry);
    if (!obb) return;

    const { width, height, thickness, center } = obb;

    // Skip degenerate meshes
    if (width <= 0 || height <= 0 || thickness <= 0) return;

    // Calculate area
    const area = width * height;
    if (area < minArea) return;

    // Filter by thickness if specified
    if (targetThickness !== null) {
      const thicknessDiff = Math.abs(thickness - targetThickness);
      if (thicknessDiff > thicknessTolerance) return;
    }

    // Check if the piece is "flat" (one dimension significantly smaller than others)
    const isFlat = thickness < width * flatnessRatio && thickness < height * flatnessRatio;

    // Also accept pieces where thickness is reasonable for sheet goods
    const isSheetGoods = thickness > minThickness && thickness < Math.min(width, height) * 0.5;

    if (!isFlat && !isSheetGoods && targetThickness === null) return;

    // Skip very thin pieces (likely rendering artifacts or 2D planes)
    if (thickness < minThickness) return;

    // Generate name: use mesh name, add component index if multiple components
    let name = meshName || `Part ${pieceId}`;
    if (componentIndex !== null) {
      name = meshName ? `${meshName} #${componentIndex + 1}` : `Part ${pieceId}`;
    }

    // Extract polygon outline for shape detection
    const polygonData = extractPolygonOutline(geometry);
    const shapeType = polygonData?.shapeType || 'rectangle';
    const vertices = polygonData?.vertices || null;

    detectedPieces.push({
      id: pieceId++,
      meshName: name,
      meshUuid: meshUuid,
      width,
      height,
      thickness,
      area,
      center,
      // Store full bounds for grain line visualization
      bounds: {
        min: obb.min.clone(),
        max: obb.max.clone(),
        size: obb.size.clone()
      },
      unit,
      selected: true,
      grainDirection: 'width', // Default grain along width (longways)
      // Shape data for non-rectangular pieces
      shapeType,
      vertices  // Normalized 2D polygon vertices relative to (0,0)
    });
  };

  // Traverse all meshes in the scene
  object.traverse((child) => {
    if (!child.isMesh || !child.geometry) return;

    // Clone geometry and apply world transform
    const geometry = child.geometry.clone();
    child.updateWorldMatrix(true, false);
    geometry.applyMatrix4(child.matrixWorld);

    // Try to separate into connected components
    // This handles Rhino and other exports that combine multiple objects into one mesh
    const components = separateConnectedComponents(geometry);

    if (components.length > 1) {
      // Multiple disconnected components found - analyze each separately
      console.log(`Mesh "${child.name || 'unnamed'}" contains ${components.length} disconnected components`);
      components.forEach((componentGeometry, index) => {
        analyzeGeometry(componentGeometry, child.name, child.uuid, index);
      });
    } else {
      // Single mesh or already separated - analyze as-is
      analyzeGeometry(geometry, child.name, child.uuid);
    }
  });

  // Sort by area (largest first)
  detectedPieces.sort((a, b) => b.area - a.area);

  return detectedPieces;
};

/**
 * Group pieces by thickness (within tolerance)
 * Tolerance is unit-aware to handle different scales properly
 */
export const groupByThickness = (pieces, unit = 'mm') => {
  // Tolerance values that make sense for each unit (roughly 1mm tolerance)
  const toleranceByUnit = {
    'm': 0.001,    // 1mm in meters
    'cm': 0.1,     // 1mm in cm
    'mm': 1,       // 1mm
    'in': 0.04,    // ~1mm in inches
    'ft': 0.003    // ~1mm in feet
  };
  const tolerance = toleranceByUnit[unit] || 1;

  const groups = [];

  pieces.forEach(piece => {
    // Find existing group with similar thickness
    let group = groups.find(g =>
      Math.abs(g.thickness - piece.thickness) <= tolerance
    );

    if (!group) {
      group = {
        thickness: piece.thickness,
        pieces: []
      };
      groups.push(group);
    }

    group.pieces.push(piece);
  });

  // Sort groups by thickness
  groups.sort((a, b) => a.thickness - b.thickness);

  // Calculate average thickness for each group
  groups.forEach(group => {
    const totalThickness = group.pieces.reduce((sum, p) => sum + p.thickness, 0);
    group.avgThickness = totalThickness / group.pieces.length;
  });

  return groups;
};

/**
 * Convert detected pieces to cut list format
 */
export const convertToCutList = (pieces, options = {}) => {
  const { modelUnit = 'mm', targetUnit = 'mm' } = options;

  // Conversion factors: model unit → mm → target unit
  const toMM = { mm: 1, cm: 10, m: 1000, 'in': 25.4, ft: 304.8 };
  const factor = (toMM[modelUnit] || 1) / (toMM[targetUnit] || 1);

  // Map 3D importer grain directions to app-standard values
  // 'height' -> 'vertical' (grain runs along height)
  // 'width' -> 'horizontal' (grain runs along width)
  const mapGrainDirection = (dir) => {
    if (dir === 'height') return 'vertical';
    if (dir === 'width') return 'horizontal';
    return dir || 'none';
  };

  return pieces
    .filter(p => p.selected)
    .map((piece, index) => ({
      id: uid(),
      name: piece.meshName || `Part ${index + 1}`,
      width: piece.height * factor,
      length: piece.width * factor,
      thickness: piece.thickness * factor,
      quantity: 1,
      material: 'Plywood',
      grainDirection: mapGrainDirection(piece.grainDirection),
      // Shape type and vertices for non-rectangular pieces
      shapeType: piece.shapeType || 'rectangle',
      vertices: piece.vertices ? piece.vertices.map(v => ({
        x: v.x * factor,
        y: v.y * factor
      })) : null,
      sourceModel: {
        thickness: piece.thickness * factor,
        center: piece.center
      }
    }));
};

/**
 * Merge similar pieces (same dimensions AND thickness within tolerance)
 */
export const mergeSimilarPieces = (pieces, tolerance = 0.5, thicknessTolerance = 0.125) => {
  const merged = [];

  pieces.forEach(piece => {
    // Use piece.thickness directly, fall back to sourceModel.thickness for backwards compatibility
    const pieceThickness = piece.thickness ?? piece.sourceModel?.thickness ?? 0;

    // Check both orientations (w×h and h×w) AND same grain direction AND same thickness
    const existing = merged.find(p => {
      const pThickness = p.thickness ?? p.sourceModel?.thickness ?? 0;
      const sameThickness = Math.abs(pThickness - pieceThickness) < thicknessTolerance;

      return sameThickness &&
        p.grainDirection === piece.grainDirection && (
          (Math.abs(p.width - piece.width) < tolerance && Math.abs(p.length - piece.length) < tolerance) ||
          (Math.abs(p.width - piece.length) < tolerance && Math.abs(p.length - piece.width) < tolerance)
        );
    });

    if (existing) {
      existing.quantity++;
    } else {
      merged.push({ ...piece, quantity: 1 });
    }
  });

  return merged;
};

export default {
  loadModel,
  analyzeModel,
  autoDetectUnit,
  getModelBounds,
  groupByThickness,
  convertToCutList,
  mergeSimilarPieces
};
